import notification from 'ant-design-vue/es/notification'
import axios from 'axios'
import request from '@/utils/request'
import { guid } from '@/utils/ruoyi.js'
import { compressImage } from '@/utils/compress-image'
import { getCachedOssSignature } from '@/utils/oss-signature-cache'

/** 直传 OSS 用独立 axios，避免携带后台 token；安装包可能较大 */
const ossAxios = axios.create({ timeout: 600000 })

/** 后台上传专用压缩参数：更小体积 = 跨境上传更快 */
const UPLOAD_COMPRESS_OPTIONS = {
  maxWidth: 1280,
  maxHeight: 1280,
  quality: 0.76,
  skipBelowKB: 150
}

function getExt(file) {
  if (file.type) {
    const parts = file.type.split('/')
    if (parts.length > 1 && parts[1]) {
      return parts[1].replace('jpeg', 'jpg')
    }
  }
  if (file.name) {
    const nameParts = file.name.split('.')
    if (nameParts.length > 1) {
      return nameParts[nameParts.length - 1]
    }
  }
  return 'jpg'
}

function buildOssKey(dir, ext) {
  const now = new Date()
  const datePath = `${now.getFullYear()}/${now.getMonth() + 1}/${now.getDate()}/`
  const fileId = Date.now() + Math.random().toString(36).slice(2, 9)
  const normalizedDir = (dir || 'upload/').endsWith('/') ? dir || 'upload/' : `${dir}/`
  return `${normalizedDir}${datePath}${fileId}.${ext}`
}

function joinOssUrl(base, key) {
  const b = String(base || '').replace(/\/+$/, '')
  const k = key.startsWith('/') ? key.slice(1) : key
  return `${b}/${k}`
}

async function uploadViaBackend(file) {
  const formData = new FormData()
  formData.append('file', file)
  const res = await request({
    url: '/common/local/file/upload',
    method: 'post',
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 600000
  })
  const url = typeof res.data === 'string' ? res.data : (res.data && res.data.url)
  if (!url) {
    throw new Error(res.message || res.msg || '上传失败，未返回文件地址')
  }
  return url
}

async function uploadViaOssDirect(file, signature) {
  const ext = getExt(file)
  const key = buildOssKey(signature.dir, ext)
  const formData = new FormData()
  formData.append('key', key)
  formData.append('policy', signature.policy)
  formData.append('OSSAccessKeyId', signature.accessid)
  formData.append('signature', signature.signature)
  formData.append('success_action_status', '200')
  formData.append('file', file)

  await ossAxios.post(signature.uploadUrl, formData)
  return joinOssUrl(signature.resultUrl, key)
}

async function prepareUploadFile(file) {
  if (file && file.type && file.type.startsWith('image/')) {
    return compressImage(file, UPLOAD_COMPRESS_OPTIONS)
  }
  return file
}

async function uploadPreparedFile(file, signature) {
  if (signature && signature.uploadUrl && signature.policy && signature.accessid) {
    try {
      return await uploadViaOssDirect(file, signature)
    } catch (e) {
      console.warn('OSS直传失败，改用服务端上传', e)
    }
  }
  return uploadViaBackend(file)
}

/**
 * 上传文件到 OSS
 * 优先：压缩 → OSS 直传（可配传输加速域名）→ 失败回退服务端
 */
export default class UploadFileToOSS {

  constructor(files, json) {
    if (!Array.isArray(files)) {
      throw new Error('Class UploadFileToOSS parameter must be an array')
    }
    if (!files || files.length === 0) {
      throw new Error('Files array cannot be empty')
    }
    this.data = []
    this.fn = json.success
    this.complete = json.finally || json.complete
    this.files = files
    this.start()
  }

  async start() {
    const validFiles = this.files.filter(Boolean)
    const [signature, ...preparedFiles] = await Promise.all([
      getCachedOssSignature().catch(() => null),
      ...validFiles.map((file) => prepareUploadFile(file))
    ])

    await Promise.all(preparedFiles.map(async (file, index) => {
      const ext = getExt(validFiles[index])
      try {
        const url = await uploadPreparedFile(file, signature)
        const item = {
          uid: guid(8, 10),
          status: 'done',
          name: `${Date.now()}.${ext}`,
          path: url,
          url,
          cover: ''
        }
        this.data.push(item)
        if (this.fn) {
          this.fn(this.data)
        }
        return item
      } catch (error) {
        console.error('OSS上传失败', error)
        notification.error({ message: '上传失败：' + (error.message || '未知错误') })
        return null
      }
    }))

    if (this.complete) {
      this.complete(this.data)
    }
  }
}

export { prefetchOssSignature } from '@/utils/oss-signature-cache'
